// Day-panel derivation for the program preview/composer.
//
// The block layer is honest about periodization: a weekly ramp is N
// truly-different prescriptions, so deriveBlocks returns N blocks — and
// a flat block list turns a 6-week program into a wall of repeated cards
// (measured on real data: 31 slots → 110 cards). The day panel is the
// readable lens: ONE entry per exercise-on-a-day, the week-splintered
// blocks merged back together, the prescription compressed to a ramp
// summary ("3×8 → 3×6 · 64→78%"). Supersets stay grouped (the block's
// members render as A1/A2 lines inside one frame).
//
// Pure module: no React, no i18n. The entry keeps its source blocks so
// the composer can wire ✎/⧉/✕ (a ramped ✎ edits per-week segments).

import type {
  Block,
  BlockMember,
  BlockSetRow,
  ComposedBlock,
  VerbatimBlock,
} from "./programBlocks.js";

// Short labels for the intensity kinds — the metric-table header set,
// reused by the panel summaries (single source; @jackedlogs/ui re-exports).
//
// This module is pure (no react-i18next), so the caller passes labels
// resolved against the active locale — same contract as ShareCardLabels.
// The defaults are the English set, so a caller that omits them renders
// exactly as before. Most kinds are locale-agnostic gym jargon (KG, RPE,
// RIR, %MAX read the same in all five locales); `history` was the one
// English-only token leaking to the family (PE-14) — but all six go
// through the same seam so a future divergence has one place to live.
// Resolve via useKindLabels() in @jackedlogs/ui, never inline.
export type KindLabels = Record<BlockSetRow["intensityKind"], string>;

export const DEFAULT_KIND_LABELS: KindLabels = {
  none: "—",
  absolute: "KG",
  "percent-of-max": "%MAX",
  history: "HIST",
  rpe: "RPE",
  rir: "RIR",
};

// One week's prescription for one member, uncompressed.
//
// `summary` gives the ramp's endpoints ("3×8 → 3×6 · 64→78%"), which is what
// a glance wants; this is every rung of the ladder, for surfaces that render
// the ramp week by week (PE-10's strip). Same fields, same scales, derived
// from the same blocks — so the two can't disagree about the same program.
//
// An inactive week still carries its `week`: the approved strip prints W{n}
// under every position, the skipped ones included. It carries nothing else,
// because there is nothing — an absent prescription must not arrive as a
// zero that renders like a real one.
//
// ⚠️ Not to be confused with `WeekCell` in programIntensity.ts: that one is
// per-DAY (all slots merged, hardest band wins) and feeds the heat grid.
// This one is per-EXERCISE. Different question, similar name.
export type DayPanelWeekCell =
  | { week: number; active: false }
  | {
      week: number;
      active: true;
      sets: number;
      reps: string;
      intensityKind: BlockSetRow["intensityKind"];
      value: string; // "" for value-less kinds (none/history)
    };

export type DayPanelMemberLine = {
  exerciseId: string;
  exerciseName: string | undefined;
  summary: string;
  // One entry per week from the member's first covered week to its last,
  // inactive weeks inside that span included (a deload that drops the
  // exercise) — see DayPanelWeekCell.
  //
  // ⛔ `cells.length` is NOT a ramp test. deriveBlocks merges every week with
  // an identical prescription into ONE block spanning them all, so a flat
  // 6-week exercise yields six identical cells, not one. To ask "does this
  // ramp?", read the entry's `blocks.length > 1` — that is the question
  // deriveBlocks actually answers.
  cells: DayPanelWeekCell[];
};

export type DayPanelComposedEntry = {
  kind: "composed";
  // The merged week-splinters, sorted by first covered week. ≥1.
  blocks: ComposedBlock[];
  grouped: boolean;
  members: DayPanelMemberLine[];
  weeks: number[]; // union across blocks, sorted
};

export type DayPanelVerbatimEntry = {
  kind: "verbatim";
  block: VerbatimBlock;
  exerciseId: string;
  exerciseName: string | undefined;
};

export type DayPanelEntry = DayPanelComposedEntry | DayPanelVerbatimEntry;

const placementOn = (b: ComposedBlock, dayIdx: number) =>
  b.placements.find((p) => p.dayIdx === dayIdx);

// Merge identity for week-splinters of the same exercise(s) on one day:
// the day's slot ids when the slots exist server-side, their session
// positions otherwise (fresh derive always carries real positions), plus
// the member sequence so a renamed slot never merges across exercises.
// Exported so the composer can re-find "the entry being ramp-edited"
// after every commit re-derives the block set.
export const dayPanelEntryKey = (b: ComposedBlock, dayIdx: number): string => {
  const p = placementOn(b, dayIdx);
  if (!p) return "";
  const slots = p.slotIds.some((id) => id !== "")
    ? `id:${p.slotIds.join(",")}`
    : `pos:${p.positions.join(",")}`;
  return `${b.members.map((m) => m.exerciseId).join("+")}|${slots}`;
};
const entryKey = dayPanelEntryKey;

const sortPosition = (e: DayPanelEntry, dayIdx: number): number => {
  if (e.kind === "verbatim") return e.block.position;
  const positions = e.blocks
    .map((b) => placementOn(b, dayIdx))
    .flatMap((p) => p?.positions ?? [])
    .filter((n) => n >= 0);
  return positions.length ? Math.min(...positions) : Number.MAX_SAFE_INTEGER;
};

// "8" when every set row prescribes the same reps, "5/3/1" otherwise.
const repsLabel = (rows: ReadonlyArray<BlockSetRow>): string => {
  const reps = rows.map((r) => r.reps.trim() || "?");
  return new Set(reps).size === 1 ? reps[0]! : reps.join("/");
};

// The member's intensity value at one block, in its row-0 kind's scale.
const valueLabel = (rows: ReadonlyArray<BlockSetRow>): string => {
  const vals = rows.map((r) => r.intensityValue.trim()).filter(Boolean);
  if (vals.length === 0) return "";
  return new Set(vals).size === 1 ? vals[0]! : vals.join("/");
};

const arrow = (first: string, last: string): string =>
  first === last ? first : `${first} → ${last}`;

// Ramp-compressed one-liner for one member across the merged blocks
// (chronological): "3×8 → 3×6 · 64→78%". Endpoints only — the glance
// wants the ramp's direction, the ✎ form shows every week.
export const memberSummary = (
  blocks: ReadonlyArray<ComposedBlock>,
  memberIdx: number,
  labels: KindLabels = DEFAULT_KIND_LABELS,
): string => {
  const first = blocks[0]?.members[memberIdx];
  const last = blocks[blocks.length - 1]?.members[memberIdx];
  if (!first || !last) return "";
  const setsReps = arrow(
    `${first.setRows.length}×${repsLabel(first.setRows)}`,
    `${last.setRows.length}×${repsLabel(last.setRows)}`,
  );
  const kind = first.setRows[0]?.intensityKind ?? "none";
  if (kind === "none") return setsReps;
  if (kind === "history") return `${setsReps} · ${labels.history}`;
  const v1 = valueLabel(first.setRows);
  const v2 = valueLabel(last.setRows);
  if (!v1 && !v2) return setsReps;
  const span = v1 === v2 ? v1 : `${v1}→${v2}`;
  const unit = kind === "percent-of-max" ? "%" : ` ${labels[kind]}`;
  return kind === "rpe" || kind === "rir"
    ? `${setsReps} · ${labels[kind]} ${span}`
    : `${setsReps} · ${span}${unit}`;
};

// Every week of one member's ramp, walking the SAME merged bucket
// memberSummary compresses — so the strip and the one-liner can never
// describe different programs. Weeks the member skips inside its own span
// come back as inactive cells rather than being closed up, because a ramp
// that drops week 3 must not read as if it ran 1-2-4 back to back.
//
// The span is walked per MEMBER rather than read off the entry's `weeks`
// union. Today the two always agree: deriveBlocks' `splitApart`
// (programBlocks.ts:183-190) tears a unit into standalone blocks as soon as
// its members' active-week sets differ, so a grouped entry's members share
// one span by construction. This stays per-member as the defensive form — if
// that invariant is ever relaxed, each member's strip shows its own ramp
// instead of the group's envelope, rather than silently mis-labelling weeks.
const memberCells = (
  blocks: ReadonlyArray<ComposedBlock>,
  memberIdx: number,
): DayPanelWeekCell[] => {
  const rowsByWeek = new Map<number, ReadonlyArray<BlockSetRow>>();
  for (const b of blocks) {
    const rows = b.members[memberIdx]?.setRows;
    if (!rows) continue;
    for (const w of b.weeks) rowsByWeek.set(w, rows);
  }
  const covered = [...rowsByWeek.keys()];
  if (covered.length === 0) return [];

  const cells: DayPanelWeekCell[] = [];
  const last = Math.max(...covered);
  for (let w = Math.min(...covered); w <= last; w++) {
    const rows = rowsByWeek.get(w);
    if (!rows) {
      cells.push({ week: w, active: false });
      continue;
    }
    cells.push({
      week: w,
      active: true,
      sets: rows.length,
      reps: repsLabel(rows),
      intensityKind: rows[0]?.intensityKind ?? "none",
      value: valueLabel(rows),
    });
  }
  return cells;
};

const memberLine = (
  blocks: ReadonlyArray<ComposedBlock>,
  memberIdx: number,
  labels: KindLabels,
): DayPanelMemberLine => {
  const m: BlockMember = blocks[0]!.members[memberIdx]!;
  return {
    exerciseId: m.exerciseId,
    exerciseName: m.exerciseName,
    summary: memberSummary(blocks, memberIdx, labels),
    cells: memberCells(blocks, memberIdx),
  };
};

// One day's panel: entries in session order, week-splinters merged,
// supersets grouped. Input is the full derived block set (the composer
// already has it; read-only hosts derive via programToDraft+deriveBlocks).
export function buildDayPanel(
  blocks: ReadonlyArray<Block>,
  dayIdx: number,
  labels: KindLabels = DEFAULT_KIND_LABELS,
): DayPanelEntry[] {
  const merged = new Map<string, ComposedBlock[]>();
  const entries: DayPanelEntry[] = [];

  for (const b of blocks) {
    if (b.kind === "verbatim") {
      if (b.dayIdx === dayIdx) {
        entries.push({
          kind: "verbatim",
          block: b,
          exerciseId: b.slot.exerciseId,
          exerciseName: b.slot.exerciseName,
        });
      }
      continue;
    }
    if (!placementOn(b, dayIdx)) continue;
    const key = entryKey(b, dayIdx);
    const bucket = merged.get(key);
    if (bucket) bucket.push(b);
    else merged.set(key, [b]);
  }

  for (const bucket of merged.values()) {
    bucket.sort((a, b) => (a.weeks[0] ?? 0) - (b.weeks[0] ?? 0));
    const head = bucket[0]!;
    entries.push({
      kind: "composed",
      blocks: bucket,
      grouped: head.members.length > 1,
      members: head.members.map((_, i) => memberLine(bucket, i, labels)),
      weeks: [...new Set(bucket.flatMap((b) => b.weeks))].sort((a, b) => a - b),
    });
  }

  entries.sort((a, b) => sortPosition(a, dayIdx) - sortPosition(b, dayIdx));
  return entries;
}
